// @flow

import { useState } from 'react';

import clsx from 'clsx';

import { Check, Plus } from 'lucide-react';

import { useDispatch } from 'react-redux';

import {
  addCartProduct,
  getCart,
  selectDomainFromFqdn,
  type ProductData,
} from '@gandi/cart';
import { mapTags, useTranslator } from '@gandi/translate';
import { Base, Button } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { useExtendedSuggestion } from '../../modules';

import styles from './XsellHosting.scss';

type Props = {
  className: string,
  fqdn: string,
  isLoading: boolean,
};

function Product({
  product,
  type,
}: {
  product: string,
  type: 'plus' | 'check',
}) {
  const Icon = type === 'plus' ? Plus : Check;
  return (
    <Base flex flexAlignItemsCenter gap="spacing-2x" weight="regular">
      <Base
        flex
        flexJustifyCenter
        flexAlignItemsCenter
        className={styles[type]}
      >
        <Icon size={16} strokeWidth={1.25} color="white" absoluteStrokeWidth />
      </Base>
      {product}
    </Base>
  );
}

const useProductData = ({ fqdn }: { fqdn: string }): ProductData => {
  const domain = useAppSelector(state => selectDomainFromFqdn(state, fqdn));

  // XXX: we may want to use the proper web hosting payload generator
  // const { getHostingPayload } = useGetHostingPayload();

  // XXX: we want to get real available applications to uese getHostingPayload() properly
  // const { availableApplications, isFetching: isFetchingApplications } =
  //   useHostingApplications();

  return {
    // $FlowFixMe[prop-missing] ProductPayload definition is a mess
    payload: {
      // $FlowFixMe[prop-missing] ProductPayload definition is a mess
      // $FlowFixMe[incompatible-exact] ProductPayload is not modern
      // $FlowFixMe[incompatible-return]
      domain: (!!domain?.fqdn && domain) || { fqdn },
      data_disk_additional_size: 0,
      duration: 12,
      label: 'Web Hosting',
      product_name: 'webhosting',
      size: 'starter',
      trial: false,
      // $FlowFixMe[incompatible-return] probably extra is not well defined
      extra: { packId: 'e544b418-d14d-475f-a40c-156b67b33393' },
    },
    process: 'create',
    product_type: 'paas',
  };
};

export function XsellHosting({
  className,
  fqdn,
  isLoading,
}: Props): React.MixedElement {
  const { __, formatPrice } = useTranslator();
  const { currency } = useExtendedSuggestion({ fqdn });

  const dispatch = useDispatch();
  const productData = useProductData({ fqdn });

  const [addingSHS, setAddingSHS] = useState(false);
  const [SHSAdded, setSHSAdded] = useState(false);

  async function handleClick() {
    setAddingSHS(true);
    try {
      await dispatch(
        addCartProduct({
          productData,
          options: {
            headers: { 'X-Dry-Run': 0 },
          },
        }),
      );
      setSHSAdded(true);
    } catch {
      /* fail silently */
    } finally {
      setAddingSHS(false);
    }

    dispatch(getCart());
  }

  const products = [__('Domain'), __('Hosting'), __('Emails')];

  return (
    <Base className={clsx(styles.root, className)}>
      <div className={styles.container}>
        <span>
          {mapTags(
            __('Upgrade this [[info:Domain with Hosting & Email]]'),
            ({ info }) => ({
              info: <strong>{info}</strong>,
            }),
          )}
        </span>

        <div className={styles.priceContainer}>
          <div className={styles.products}>
            {products.map((product, i) => (
              <Product product={product} type={i !== 0 ? 'plus' : 'check'} />
            ))}
          </div>
          <div className={styles.price}>
            {/** TODO: Use mapTags and change everywhere */}
            {formatPrice(2, currency)}/
            {__(
              '<for-translators-use>Periodicity (month)</for-translators-use>mo',
            )}
          </div>
        </div>
      </div>
      <Button
        className={styles.button}
        type="button"
        onClick={
          SHSAdded
            ? undefined /* we may want to use the `disabled` prop instead,
                             but the rendering become too dark */
            : handleClick
        }
        loading={isLoading || addingSHS}
        iconBefore={SHSAdded ? 'checkmark' : undefined}
      >
        {SHSAdded ? __('Added') : __('Get the deal')}
      </Button>
    </Base>
  );
}
